import React, { useEffect, useRef } from "react";
import { Animated, TouchableOpacity, Easing, ViewStyle } from "react-native";
import { Chevron } from "./Chevron";
import { PALETTE } from "../lib/palette";
import apps from "../lib/apps";

/**
 * 헤더 백/X 버튼용 원형 칩(매장 accent 배경 + 옅은 그림자 + 헤어라인 테두리). 흰 헤더에서도
 * 버튼(그리고 blink)이 도드라지도록. 배경 accent는 매장별 동적 색이라 런타임에 주입한다.
 * 백버튼·닫기(X) 버튼이 공유한다.
 */
export const CIRCLE_BTN: ViewStyle = {
    width: 30,
    height: 30,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#141820",
    shadowOpacity: 0.16,
    shadowRadius: 5,
    shadowOffset: { width: 0, height: 2 },
    elevation: 3,
};

// 원형 칩 바깥 테두리(아주 옅은 검정 헤어라인) — accent 채움 원의 가장자리를 어떤 배경에서도 또렷하게.
export const CIRCLE_BORDER: ViewStyle = { borderWidth: 1, borderColor: "rgba(0,0,0,0.10)" };

// 매장 accent(동적). 매장 미로딩 시 기본 accent로 폴백.
export function safeAccent(): string {
    try { return apps.getAccent(); } catch (e) { return PALETTE.ACCENT; }
}

// 백/X 버튼 공용: 원형 칩 + accent 배경 + 헤어라인 테두리(스타일 배열).
export function circleBtnAccent(accent: string) {
    return [CIRCLE_BTN, { backgroundColor: accent }, CIRCLE_BORDER];
}

/**
 * 셰브론(‹) 뒤로가기 버튼. blink=true면 은은한 지속 펄스(opacity loop)로 시선을 끌어
 * 고객이 폰 뒤로가기 대신 앱의 back 버튼을 쓰도록 유도한다(상세메뉴·Order Details·Search).
 * 배경은 매장 accent, 셰브론은 흰색. containerStyle로 위치 등 추가 지정. RN-web이라 useNativeDriver:false.
 */
export function BackButton({
    onPress,
    containerStyle,
    size = 12,
    thickness = 2.5,
    color = "#ffffff",
    hitSlop,
    blink = true,
}: {
    onPress: () => void;
    containerStyle?: any;
    size?: number;
    thickness?: number;
    color?: string;
    hitSlop?: { top: number; bottom: number; left: number; right: number };
    blink?: boolean;
}) {
    const pulse = useRef(new Animated.Value(1)).current;
    const accent = safeAccent();

    useEffect(() => {
        if (!blink) return;
        const loop = Animated.loop(
            Animated.sequence([
                Animated.timing(pulse, { toValue: 0.35, duration: 900, easing: Easing.inOut(Easing.ease), useNativeDriver: false }),
                Animated.timing(pulse, { toValue: 1, duration: 900, easing: Easing.inOut(Easing.ease), useNativeDriver: false }),
            ])
        );
        loop.start();
        return () => loop.stop();
    }, [blink]);

    return (
        <TouchableOpacity onPress={onPress} hitSlop={hitSlop}>
            <Animated.View style={[containerStyle, { backgroundColor: accent }, CIRCLE_BORDER, blink ? { opacity: pulse } : null]}>
                <Chevron direction="left" size={size} thickness={thickness} color={color} />
            </Animated.View>
        </TouchableOpacity>
    );
}
